import { normalizeMentionItem, type MentionSuggestionItem } from "@seavien/commons";
import type {
    AjaxPaginationControllerApi,
    UserAsFollowingDTO,
    UserParticipatingInDiscussionDTO,
    UserProfileDTO,
} from "@/generated-client";
import { ClientFactory } from "@/main/common/ClientFactory";
import { MetaHandler } from "@/main/common/MetaHandler";

export const USER_SUGGESTION_REQUEST_DEBOUNCE_MS = 300;
export const MIN_REMOTE_USER_SUGGESTION_QUERY_LENGTH = 3;

const QUERY_CACHE_TTL_MS = 10 * 60 * 1000;
const NEGATIVE_QUERY_CACHE_TTL_MS = 2 * 60 * 1000;
const FAILURE_RETRY_DELAY_MS = 5_000;
const RELATED_USER_TTL_MS = 30 * 60 * 1000;
const MAX_QUERY_CACHE_ENTRIES = 150;
const MAX_RELATED_USERS = 200;
const MAX_PRELOADED_FOLLOWINGS = 40;
const MAX_PRELOADED_DISCUSSION_PARTICIPANTS = 30;

type RelatedUserSource = "selected" | "recentChatThread" | "context" | "discussionParticipant" | "following" | "remote";

type QueryCacheEntry = {
    items: MentionSuggestionItem[];
    expiresAt: number;
};

type RelatedUserEntry = {
    item: MentionSuggestionItem;
    priority: number;
    lastUpdatedAt: number;
    expiresAt: number;
};

export type UserSuggestionContext = {
    discussionPublicId?: string;
    relatedUsers?: MentionSuggestionItem[];
};

export type UserSuggestionSnapshot = {
    items: MentionSuggestionItem[];
    shouldFetchRemote: boolean;
};

const SOURCE_PRIORITY: Record<RelatedUserSource, number> = {
    selected: 600,
    recentChatThread: 500,
    context: 450,
    discussionParticipant: 400,
    following: 350,
    remote: 200,
};

function normalizeSuggestionQuery(query: unknown): string {
    return String(query ?? "").trim().replace(/^@+/, "");
}

function mapUserLikeToMentionItem(
    user: Pick<UserProfileDTO | UserAsFollowingDTO | UserParticipatingInDiscussionDTO, "userId" | "username" | "profileImageUrl" | "description">,
): MentionSuggestionItem | null {
    const username = typeof user?.username === "string" ? user.username.trim() : "";
    if (!username) {
        return null;
    }

    return normalizeMentionItem({
        id: user?.userId != null ? String(user.userId) : username,
        username,
        label: username,
        profileImageUrl: user.profileImageUrl ?? undefined,
        description: user.description ?? undefined,
    });
}

function trimMapToSize<T>(map: Map<string, T>, maxSize: number) {
    while (map.size > maxSize) {
        const oldestKey = map.keys().next().value;
        if (typeof oldestKey !== "string") {
            return;
        }
        map.delete(oldestKey);
    }
}

function matchesSuggestionQuery(item: MentionSuggestionItem, query: string): boolean {
    const normalizedItem = normalizeMentionItem(item);
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
        return true;
    }

    return [normalizedItem.username, normalizedItem.label, normalizedItem.description]
        .filter((value): value is string => typeof value === "string" && value.trim().length > 0)
        .some((value) => value.toLowerCase().includes(normalizedQuery));
}

function getSuggestionScore(item: MentionSuggestionItem, normalizedQuery: string, priority: number): number {
    const username = String(item.username ?? "").toLowerCase();
    const label = String(item.label ?? "").toLowerCase();
    const description = String(item.description ?? "").toLowerCase();

    let score = priority * 100;

    if (username === normalizedQuery) {
        score += 100;
    } else if (username.startsWith(normalizedQuery)) {
        score += 70;
    } else if (label.startsWith(normalizedQuery)) {
        score += 60;
    } else if (username.includes(normalizedQuery)) {
        score += 45;
    } else if (label.includes(normalizedQuery)) {
        score += 35;
    }

    if (description.startsWith(normalizedQuery)) {
        score += 10;
    } else if (description.includes(normalizedQuery)) {
        score += 5;
    }

    return score;
}

class UserSuggestionService {
    private api: AjaxPaginationControllerApi | null = null;

    private readonly queryCache = new Map<string, QueryCacheEntry>();
    private readonly recentQueryFailures = new Map<string, number>();
    private readonly inFlightSearches = new Map<string, Promise<MentionSuggestionItem[]>>();
    private readonly relatedUsers = new Map<string, RelatedUserEntry>();
    private viewerFollowingsPreloadPromise: Promise<void> | null = null;
    private viewerFollowingsLoadedForUsername: string | null = null;
    private readonly discussionPreloadPromises = new Map<string, Promise<void>>();
    private readonly discussionPreloaded = new Set<string>();

    private getApi(): AjaxPaginationControllerApi {
        if (!this.api) {
            this.api = ClientFactory.getAjaxPaginationRestControllerApi();
        }

        return this.api;
    }

    private pruneExpiredEntries() {
        const now = Date.now();

        for (const [key, entry] of this.queryCache.entries()) {
            if (entry.expiresAt <= now) {
                this.queryCache.delete(key);
            }
        }

        for (const [key, entry] of this.relatedUsers.entries()) {
            if (entry.expiresAt <= now) {
                this.relatedUsers.delete(key);
            }
        }

        for (const [key, failedAt] of this.recentQueryFailures.entries()) {
            if ((now - failedAt) > FAILURE_RETRY_DELAY_MS) {
                this.recentQueryFailures.delete(key);
            }
        }
    }

    private getFreshQueryCacheEntry(cacheKey: string): QueryCacheEntry | null {
        this.pruneExpiredEntries();
        const entry = this.queryCache.get(cacheKey);
        if (!entry) {
            return null;
        }

        if (entry.expiresAt <= Date.now()) {
            this.queryCache.delete(cacheKey);
            return null;
        }

        return entry;
    }

    private setQueryCacheEntry(cacheKey: string, items: MentionSuggestionItem[]) {
        const normalizedItems = items.map((item) => normalizeMentionItem(item));
        this.queryCache.set(cacheKey, {
            items: normalizedItems,
            expiresAt: Date.now() + (normalizedItems.length > 0 ? QUERY_CACHE_TTL_MS : NEGATIVE_QUERY_CACHE_TTL_MS),
        });
        trimMapToSize(this.queryCache, MAX_QUERY_CACHE_ENTRIES);
    }

    private getRelatedUserKey(item: MentionSuggestionItem): string {
        const normalizedItem = normalizeMentionItem(item);
        return (normalizedItem.username || normalizedItem.id).toLowerCase();
    }

    seedRelatedUsers(items: MentionSuggestionItem[], source: RelatedUserSource = "context") {
        if (!items.length) {
            return;
        }

        this.pruneExpiredEntries();
        const priority = SOURCE_PRIORITY[source];
        const now = Date.now();

        for (const rawItem of items) {
            const item = normalizeMentionItem(rawItem);
            if (!item.username) {
                continue;
            }

            const key = this.getRelatedUserKey(item);
            const existing = this.relatedUsers.get(key);
            this.relatedUsers.set(key, {
                item: {
                    ...existing?.item,
                    ...item,
                },
                priority: Math.max(existing?.priority ?? 0, priority),
                lastUpdatedAt: now,
                expiresAt: now + RELATED_USER_TTL_MS,
            });
        }

        trimMapToSize(this.relatedUsers, MAX_RELATED_USERS);
    }

    rememberSelectedUser(item: MentionSuggestionItem) {
        this.seedRelatedUsers([item], "selected");
    }

    private getLocalMatches(query: string, maxResults: number): MentionSuggestionItem[] {
        this.pruneExpiredEntries();
        const normalizedQuery = query.toLowerCase();

        return [...this.relatedUsers.values()]
            .filter((entry) => matchesSuggestionQuery(entry.item, normalizedQuery))
            .sort((left, right) => {
                const scoreDifference = getSuggestionScore(right.item, normalizedQuery, right.priority)
                    - getSuggestionScore(left.item, normalizedQuery, left.priority);
                if (scoreDifference !== 0) {
                    return scoreDifference;
                }

                if (right.priority !== left.priority) {
                    return right.priority - left.priority;
                }

                if (right.lastUpdatedAt !== left.lastUpdatedAt) {
                    return right.lastUpdatedAt - left.lastUpdatedAt;
                }

                return String(left.item.username ?? left.item.id).localeCompare(String(right.item.username ?? right.item.id));
            })
            .slice(0, maxResults)
            .map((entry) => normalizeMentionItem(entry.item));
    }

    ensurePreloaded(context: UserSuggestionContext = {}) {
        if (context.relatedUsers?.length) {
            this.seedRelatedUsers(context.relatedUsers, "context");
        }

        void this.ensureViewerFollowingsPreloaded();

        if (context.discussionPublicId) {
            void this.ensureDiscussionParticipantsPreloaded(context.discussionPublicId);
        }
    }

    private async ensureViewerFollowingsPreloaded() {
        const username = MetaHandler.getLoggedInUsername?.() ?? null;
        if (!username) {
            return;
        }

        if (this.viewerFollowingsLoadedForUsername === username) {
            return;
        }

        if (this.viewerFollowingsPreloadPromise) {
            return this.viewerFollowingsPreloadPromise;
        }

        this.viewerFollowingsPreloadPromise = this.getApi()
            .newFollowingsForUser(username)
            .then((response) => {
                const items = (response?.data ?? [])
                    .map(mapUserLikeToMentionItem)
                    .filter((item): item is MentionSuggestionItem => Boolean(item))
                    .slice(0, MAX_PRELOADED_FOLLOWINGS);
                this.seedRelatedUsers(items, "following");
                this.viewerFollowingsLoadedForUsername = username;
            })
            .catch(() => {
            })
            .finally(() => {
                this.viewerFollowingsPreloadPromise = null;
            });

        return this.viewerFollowingsPreloadPromise;
    }

    private async ensureDiscussionParticipantsPreloaded(discussionPublicId: string) {
        const normalizedDiscussionPublicId = discussionPublicId.trim();
        if (!normalizedDiscussionPublicId || this.discussionPreloaded.has(normalizedDiscussionPublicId)) {
            return;
        }

        if (this.discussionPreloadPromises.has(normalizedDiscussionPublicId)) {
            return this.discussionPreloadPromises.get(normalizedDiscussionPublicId);
        }

        const preloadPromise = this.getApi()
            .usersParticipatingInDiscussion(normalizedDiscussionPublicId)
            .then((response) => {
                const items = (response?.data ?? [])
                    .map(mapUserLikeToMentionItem)
                    .filter((item): item is MentionSuggestionItem => Boolean(item))
                    .slice(0, MAX_PRELOADED_DISCUSSION_PARTICIPANTS);
                this.seedRelatedUsers(items, "discussionParticipant");
                this.discussionPreloaded.add(normalizedDiscussionPublicId);
            })
            .catch(() => {
            })
            .finally(() => {
                this.discussionPreloadPromises.delete(normalizedDiscussionPublicId);
            });

        this.discussionPreloadPromises.set(normalizedDiscussionPublicId, preloadPromise);
        return preloadPromise;
    }

    getSuggestionSnapshot(query: string, maxResults: number, context: UserSuggestionContext = {}): UserSuggestionSnapshot {
        this.ensurePreloaded(context);

        const normalizedQuery = normalizeSuggestionQuery(query);
        if (!normalizedQuery) {
            // A bare "@" shows the ranked roster rather than nothing (2026-09-01). Everything needed is
            // already in memory: ensurePreloaded() above has seeded `relatedUsers` from the viewer's
            // followings, the discussion's participants and any caller-supplied context, ranked by
            // SOURCE_PRIORITY. matchesSuggestionQuery already returns true for an empty query and
            // getSuggestionScore already degrades to priority order, so this is a deleted guard, not new
            // ranking. Never remote: an empty query has nothing to search for, and MIN_REMOTE_USER_
            // SUGGESTION_QUERY_LENGTH would refuse it anyway.
            return {
                items: this.getLocalMatches("", maxResults),
                shouldFetchRemote: false,
            };
        }

        const cacheKey = normalizedQuery.toLowerCase();
        const cacheEntry = this.getFreshQueryCacheEntry(cacheKey);
        if (cacheEntry) {
            return {
                items: cacheEntry.items.slice(0, maxResults),
                shouldFetchRemote: false,
            };
        }

        return {
            items: this.getLocalMatches(normalizedQuery, maxResults),
            shouldFetchRemote: normalizedQuery.length >= MIN_REMOTE_USER_SUGGESTION_QUERY_LENGTH,
        };
    }

    async fetchRemoteSuggestions(query: string, maxResults: number, context: UserSuggestionContext = {}): Promise<MentionSuggestionItem[]> {
        this.ensurePreloaded(context);

        const normalizedQuery = normalizeSuggestionQuery(query);
        if (!normalizedQuery) {
            return [];
        }

        if (normalizedQuery.length < MIN_REMOTE_USER_SUGGESTION_QUERY_LENGTH) {
            return this.getLocalMatches(normalizedQuery, maxResults);
        }

        const cacheKey = normalizedQuery.toLowerCase();
        const cacheEntry = this.getFreshQueryCacheEntry(cacheKey);
        if (cacheEntry) {
            return cacheEntry.items.slice(0, maxResults);
        }

        const failedAt = this.recentQueryFailures.get(cacheKey);
        if (typeof failedAt === "number" && (Date.now() - failedAt) < FAILURE_RETRY_DELAY_MS) {
            return this.getLocalMatches(normalizedQuery, maxResults);
        }

        const existingPromise = this.inFlightSearches.get(cacheKey);
        if (existingPromise) {
            return existingPromise.then((items) => items.slice(0, maxResults));
        }

        const requestPromise = this.getApi()
            .searchUsersForMentionSuggestions(normalizedQuery)
            .then((response) => {
                const items = (response?.data ?? [])
                    .map(mapUserLikeToMentionItem)
                    .filter((item): item is MentionSuggestionItem => Boolean(item))
                    .slice(0, maxResults);
                this.setQueryCacheEntry(cacheKey, items);
                this.seedRelatedUsers(items, "remote");
                this.recentQueryFailures.delete(cacheKey);
                return items;
            })
            .catch((error) => {
                this.recentQueryFailures.set(cacheKey, Date.now());
                throw error;
            })
            .finally(() => {
                this.inFlightSearches.delete(cacheKey);
            });

        this.inFlightSearches.set(cacheKey, requestPromise);
        return requestPromise;
    }

    resetForTests() {
        this.queryCache.clear();
        this.recentQueryFailures.clear();
        this.inFlightSearches.clear();
        this.relatedUsers.clear();
        this.viewerFollowingsPreloadPromise = null;
        this.viewerFollowingsLoadedForUsername = null;
        this.discussionPreloadPromises.clear();
        this.discussionPreloaded.clear();
        this.api = null;
    }
}

export const userSuggestionService = new UserSuggestionService();
